import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';

// FAQ data targeting top Google searches - visible paragraph format for SEO
const faqData = {
  en: [
    {
      question: "Is NaLiVox an AI interview assistant that helps in real-time?",
      answer: "Yes! NaLiVox is a real-time AI interview assistant (interview copilot) that listens to your video calls and provides intelligent suggestions as you speak. Upload your CV and preparation notes to the Knowledge Base, and the AI will suggest relevant answers based on your experience. Perfect for job interviews, technical interviews, and high-stakes conversations where you need instant support."
    },
    {
      question: "How does real-time meeting transcription work without a bot joining?",
      answer: "Unlike Otter.ai or Fireflies, NaLiVox doesn't add a bot to your meeting. Instead, you share your screen with audio enabled, and our AI transcribes everything in real-time directly in your browser. No one in the meeting knows you're using it - there's no 'NaLiVox Bot has joined' notification. Your privacy is protected while you get accurate live transcription."
    },
    {
      question: "Can NaLiVox generate AI meeting notes and summaries automatically?",
      answer: "Absolutely! NaLiVox creates AI meeting notes with one click. Get structured summaries including key points discussed, decisions made, action items, and follow-ups. Unlike other AI meeting assistants, NaLiVox works with ANY video platform - Zoom, Microsoft Teams, Google Meet, Discord, or any browser-based call. No integrations or permissions needed."
    },
    {
      question: "How can I get help during a video interview?",
      answer: "NaLiVox is designed exactly for this. Start the app before your interview, share your screen with audio, and the AI listens in real-time. When you're asked a question, the AI can suggest answers based on your uploaded CV and notes. You can also type questions to the AI chat like 'What should I say about my experience with Python?' and get instant, contextual responses."
    },
    {
      question: "Does NaLiVox transcribe Google Meet, Teams, and Zoom calls?",
      answer: "Yes! NaLiVox transcribes Google Meet, Microsoft Teams, Zoom, Webex, Discord, and any other video platform. Because it uses screen sharing with audio capture (not a bot), it works universally. Just click 'Share Screen', select the tab or window with your meeting, enable 'Share audio', and transcription starts instantly in 99+ languages."
    },
    {
      question: "Is there an AI that answers questions in real-time during meetings?",
      answer: "That's NaLiVox's killer feature! Our AI listens to your conversation and you can ask it anything in real-time: 'What numbers were just mentioned?', 'Summarize the last 5 minutes', 'What should I respond to this question?'. The AI understands the full context of your meeting and gives you instant, accurate answers while you're still in the call."
    },
    {
      question: "How is NaLiVox different from meeting transcription with bots?",
      answer: "Traditional tools like Otter, Fireflies, and Grain add a visible bot to your meetings, which can be awkward or prohibited in some companies. NaLiVox uses browser-based audio capture - no bot joins your call, no permissions needed from meeting hosts, and your transcription stays 100% private in your browser. The audio is processed in real-time and never stored on our servers."
    },
    {
      question: "Can NaLiVox help with technical interviews and coding interviews?",
      answer: "NaLiVox is perfect for technical interviews and coding interview preparation. Upload your resume, technical notes, or documentation to the Knowledge Base. During your interview, ask the AI to help explain concepts, remind you of syntax, or suggest how to approach a problem. Many developers use NaLiVox to land jobs at FAANG companies and top tech firms by having an AI assistant that understands their technical background."
    }
  ],
  fr: [
    {
      question: "NaLiVox est-il un assistant IA pour entretiens qui aide en temps reel ?",
      answer: "Oui ! NaLiVox est un assistant IA pour entretiens (interview copilot) qui ecoute vos appels video et fournit des suggestions intelligentes pendant que vous parlez. Telechargez votre CV et vos notes de preparation dans la Base de Connaissances, et l'IA vous suggere des reponses pertinentes basees sur votre experience. Parfait pour les entretiens d'embauche, techniques, et les conversations importantes ou vous avez besoin d'un soutien instantane."
    },
    {
      question: "Comment fonctionne la transcription en temps reel sans bot ?",
      answer: "Contrairement a Otter.ai ou Fireflies, NaLiVox n'ajoute pas de bot a votre reunion. Vous partagez simplement votre ecran avec l'audio active, et notre IA transcrit tout en temps reel directement dans votre navigateur. Personne dans la reunion ne sait que vous l'utilisez - pas de notification 'NaLiVox Bot a rejoint'. Votre vie privee est protegee tout en obtenant une transcription live precise."
    },
    {
      question: "NaLiVox peut-il generer des notes de reunion et resumes automatiquement ?",
      answer: "Absolument ! NaLiVox cree des notes de reunion IA en un clic. Obtenez des resumes structures incluant les points cles discutes, les decisions prises, les actions a suivre. Contrairement aux autres assistants IA, NaLiVox fonctionne avec TOUTES les plateformes video - Zoom, Microsoft Teams, Google Meet, Discord, ou tout appel via navigateur. Aucune integration ou permission requise."
    },
    {
      question: "Comment obtenir de l'aide pendant un entretien video ?",
      answer: "NaLiVox est concu exactement pour cela. Lancez l'application avant votre entretien, partagez votre ecran avec l'audio, et l'IA ecoute en temps reel. Quand on vous pose une question, l'IA peut suggerer des reponses basees sur votre CV et notes telecharges. Vous pouvez aussi poser des questions au chat IA comme 'Que dois-je dire sur mon experience Python ?' et obtenir des reponses instantanees et contextuelles."
    },
    {
      question: "NaLiVox transcrit-il Google Meet, Teams et Zoom ?",
      answer: "Oui ! NaLiVox transcrit Google Meet, Microsoft Teams, Zoom, Webex, Discord, et toute autre plateforme video. Comme il utilise le partage d'ecran avec capture audio (pas un bot), il fonctionne universellement. Cliquez sur 'Partager l'ecran', selectionnez l'onglet avec votre reunion, activez 'Partager l'audio', et la transcription demarre instantanement en plus de 99 langues."
    },
    {
      question: "Existe-t-il une IA qui repond aux questions en temps reel pendant les reunions ?",
      answer: "C'est LA fonctionnalite phare de NaLiVox ! Notre IA ecoute votre conversation et vous pouvez lui poser n'importe quelle question en temps reel : 'Quels chiffres ont ete mentionnes ?', 'Resume les 5 dernieres minutes', 'Que dois-je repondre a cette question ?'. L'IA comprend tout le contexte de votre reunion et vous donne des reponses instantanees et precises pendant l'appel."
    },
    {
      question: "Quelle est la difference entre NaLiVox et les transcriptions avec bot ?",
      answer: "Les outils traditionnels comme Otter, Fireflies et Grain ajoutent un bot visible a vos reunions, ce qui peut etre genant ou interdit dans certaines entreprises. NaLiVox utilise la capture audio du navigateur - aucun bot ne rejoint votre appel, aucune permission necessaire de l'organisateur, et votre transcription reste 100% privee dans votre navigateur. L'audio est traite en temps reel et jamais stocke sur nos serveurs."
    },
    {
      question: "NaLiVox peut-il aider pour les entretiens techniques et de coding ?",
      answer: "NaLiVox est parfait pour les entretiens techniques et la preparation aux entretiens de coding. Telechargez votre CV, notes techniques ou documentation dans la Base de Connaissances. Pendant votre entretien, demandez a l'IA d'expliquer des concepts, de vous rappeler une syntaxe, ou de suggerer comment aborder un probleme. De nombreux developpeurs utilisent NaLiVox pour decrocher des postes chez les GAFAM et grandes entreprises tech grace a un assistant IA qui comprend leur parcours technique."
    }
  ]
};

// Single FAQ item - visible paragraph format (no accordion)
function FAQItem({ question, answer, index, isInView }) {
  return (
    <motion.article
      className="mb-8 last:mb-0"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
    >
      <h3 className="text-lg md:text-xl font-semibold text-white mb-3">
        {question}
      </h3>
      <p className="text-gray-400 leading-relaxed">
        {answer}
      </p>
    </motion.article>
  );
}

function FAQ() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [language, setLanguage] = useState('en');

  // Auto-detect browser language on mount
  useEffect(() => {
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang && browserLang.toLowerCase().startsWith('fr')) {
      setLanguage('fr');
    }
  }, []);

  const questions = faqData[language];

  return (
    <section
      id="faq"
      className="snap-section relative min-h-screen flex items-center py-20"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-dark/50 to-dark z-0" />

      <div className="max-w-[1800px] mx-auto px-6 md:px-12 w-full relative z-10">
        {/* Header */}
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="section-label">FAQ</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            {language === 'fr' ? (
              <>Questions <span className="gradient-text">frequentes</span></>
            ) : (
              <>Frequently asked <span className="gradient-text">questions</span></>
            )}
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed max-w-3xl mx-auto mb-4">
            {language === 'fr'
              ? "Tout ce que vous devez savoir sur NaLiVox - l'assistant IA pour entretiens et reunions en temps reel."
              : "Everything you need to know about NaLiVox - the real-time AI assistant for interviews and meetings."
            }
          </p>

          {/* Language switcher */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span>
              {language === 'fr' ? 'Affiche en francais' : 'Showing in English'}
            </span>
            <button
              onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
              className="ml-2 text-accent-cyan hover:text-accent-magenta transition-colors underline underline-offset-2"
            >
              {language === 'fr' ? 'Switch to English' : 'Passer en francais'}
            </button>
          </div>
        </motion.div>

        {/* FAQ grid - 2 columns on desktop */}
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-2">
          {/* Left column */}
          <div className="bg-white/[0.02] backdrop-blur-sm rounded-2xl border border-white/10 p-6 md:p-8">
            {questions.slice(0, 4).map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
                index={index}
                isInView={isInView}
              />
            ))}
          </div>

          {/* Right column */}
          <div className="bg-white/[0.02] backdrop-blur-sm rounded-2xl border border-white/10 p-6 md:p-8">
            {questions.slice(4, 8).map((item, index) => (
              <FAQItem
                key={index + 4}
                question={item.question}
                answer={item.answer}
                index={index + 4}
                isInView={isInView}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default FAQ;
